import {
  type Category,
  type Config,
  type ConfigSection,
  NEW_ITEM_DEFAULTS,
  newConfigId,
  type Priority,
  type PublicUser,
  type Role,
  type Status,
  type UserBatchInput,
} from '@hrms/shared'

export interface PersonRow {
  /** Real user id, or `new:…` for someone added in this draft. */
  id: string
  email: string
  name: string
  dept: string
  role: Role
  isHrLead: boolean
  archived: boolean
  avatarUrl: string | null
  isNew?: boolean
  /** Server `updatedAt` the draft started from (sent with the batch for the conflict check). */
  updatedAt?: string
}

export interface SettingsDraft {
  categories: Category[]
  priorities: Priority[]
  statuses: Status[]
  people: PersonRow[]
}

export type ListSection = Exclude<ConfigSection, 'people'>

export function toPersonRow(u: PublicUser): PersonRow {
  return {
    id: u.id,
    email: u.email,
    name: u.name,
    dept: u.dept ?? '',
    role: u.role,
    isHrLead: u.role === 'admin' && u.isHrLead,
    archived: u.archived,
    avatarUrl: u.avatarUrl,
    updatedAt: u.updatedAt,
  }
}

export function baselineFrom(config: Config, users: PublicUser[]): SettingsDraft {
  return {
    categories: config.categories,
    priorities: config.priorities,
    statuses: config.statuses,
    people: users.map(toPersonRow),
  }
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

export function dirtySections(draft: SettingsDraft, base: SettingsDraft): Set<ConfigSection> {
  const out = new Set<ConfigSection>()
  for (const key of ['categories', 'priorities', 'statuses', 'people'] as const)
    if (!same(draft[key], base[key])) out.add(key)
  return out
}

/** Swap item `index` with its neighbour (`dir` −1 up, +1 down). */
export function move<T>(items: readonly T[], index: number, dir: -1 | 1): T[] {
  const j = index + dir
  if (j < 0 || j >= items.length) return [...items]
  const next = [...items]
  const a = next[index] as T
  next[index] = next[j] as T
  next[j] = a
  return next
}

export function newItem(section: 'categories'): Category
export function newItem(section: 'priorities'): Priority
export function newItem(section: 'statuses'): Status
export function newItem(section: ListSection): Category | Priority | Status
export function newItem(section: ListSection): Category | Priority | Status {
  if (section === 'categories') {
    const d = NEW_ITEM_DEFAULTS.category
    return { id: newConfigId(d.name), ...d, archived: false }
  }
  if (section === 'priorities') {
    const d = NEW_ITEM_DEFAULTS.priority
    return { id: newConfigId(d.name), ...d, archived: false }
  }
  const d = NEW_ITEM_DEFAULTS.status
  return { id: newConfigId(d.name), ...d, archived: false }
}

/** Turn the People draft into a `POST /api/users/batch` body (only changed fields). */
export function peopleBatch(draft: PersonRow[], base: PersonRow[]): UserBatchInput {
  const byId = new Map(base.map((p) => [p.id, p]))
  const updates: NonNullable<UserBatchInput['updates']> = []
  const creates: NonNullable<UserBatchInput['creates']> = []
  for (const p of draft) {
    const isHrLead = p.role === 'admin' && p.isHrLead
    if (p.isNew) {
      creates.push({
        email: p.email,
        name: p.name.trim(),
        dept: p.dept.trim() || null,
        role: p.role,
        isHrLead,
      })
      continue
    }
    const b = byId.get(p.id)
    if (!b) continue
    const u: (typeof updates)[number] = { id: p.id }
    if (p.name.trim() !== b.name) u.name = p.name.trim()
    if (p.dept.trim() !== b.dept) u.dept = p.dept.trim() || null
    if (p.role !== b.role) u.role = p.role
    if (isHrLead !== b.isHrLead) u.isHrLead = isHrLead
    if (p.archived !== b.archived) u.archived = p.archived
    if (Object.keys(u).length > 1) updates.push(b.updatedAt ? { ...u, updatedAt: b.updatedAt } : u)
  }
  return { updates, creates }
}

/** "jane.doe@x.com" → "Jane Doe". */
export function nameFromEmail(email: string): string {
  return (email.split('@')[0] ?? '')
    .split(/[._-]+/)
    .filter(Boolean)
    .map((p) => (p[0]?.toUpperCase() ?? '') + p.slice(1))
    .join(' ')
}
